import React from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Dropdown,
  Field,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  ProgressBar,
  Skeleton,
  SkeletonItem,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  Toaster,
  Tooltip,
  mergeClasses,
  useId,
  useToastController,
} from '@fluentui/react-components';
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideTable, GuideTh, guideCellClassName as cell, guideRowClassName } from '../../../theme/components/GuideTable';
import {
  ArrowClockwise20Regular,
  ArrowSync16Regular,
  ArrowDownload20Regular,
  ArrowUpload24Regular,
  Checkmark16Regular,
  CheckmarkCircle16Regular,
  Clock16Regular,
  Delete20Regular,
  DocumentCheckmark20Regular,
  DocumentBulletList24Regular,
  DocumentDismiss16Regular,
  ErrorCircle16Regular,
  Eye20Regular,
  Link16Regular,
  Link20Regular,
  LinkDismiss20Regular,
  Warning16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as ComponentFunctions from './AzureFileUploadDrawer.Functions';
import { useAzureFileUploadDrawerStyles } from './AzureFileUploadDrawer.Styles';
import { useFileDropTarget } from '../FileDropZone/useFileDropTarget';
import { ViewInFilesLink } from '../../Files/ViewInFilesLink/ViewInFilesLink.Component';
import FilePreview from '../../Files/FilePreview/FilePreview.Component';
import FilePickerDialog from '../../Files/FilePickerDialog/FilePickerDialog.Component';
import { ConfirmActionDialog } from '../../Files/StorageDialogs/ConfirmActionDialog.Component';
import { PreviewFile, getFriendlyFileName } from '../../Files/FilePreview/FilePreview.Functions';
import { triggerDownload } from '../../Files/FilesExplorer/FilesExplorer.Functions';
import {
  AZURE_UPLOAD_RECHECK_DELAYS_MS,
  buildAcceptAttribute,
  formatFileSize,
  canLinkFiles,
  getRegisterKind,
  getTargetStorageFolders,
  listAllowedExtensions,
  mergeDrawerRows,
} from '../../../functions/AzureFileUpload.Functions';
import { Translate, buildFriendlyLocation, getOwnerFolderPath } from '../../../functions/StorageBrowser.Functions';
import { describeStorageError } from '../../../functions/StorageManagement.Functions';
import { AzureFileTypeEnum } from '../../../models/Enums/AzureFileTypeEnum';
import { DrawerStorageFile } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { ErrorType } from '../../../models/HttpError';

// Status pills: outline + icon + text (state never conveyed by color alone).
const statusBadge: Record<
  ComponentFunctions.UploadItemStatus,
  { color: 'informative' | 'brand' | 'success' | 'warning' | 'danger'; icon: JSX.Element }
> = {
  pending: { color: 'informative', icon: <Clock16Regular /> },
  uploading: { color: 'brand', icon: <ArrowSync16Regular /> },
  uploaded: { color: 'success', icon: <Checkmark16Regular /> },
  unregistered: { color: 'warning', icon: <Warning16Regular /> },
  error: { color: 'danger', icon: <ErrorCircle16Regular /> },
};

export const AzureFileUploadDrawerComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useAzureFileUploadDrawerStyles();
  const toasterId = useId('azure-upload-toaster');
  const { dispatchToast } = useToastController(toasterId);

  const allowMultiple = props.allowMultiple ?? true;
  const [items, setItems] = React.useState<ComponentFunctions.UploadItem[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const [showRegisteringNotice, setShowRegisteringNotice] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const mountedRef = React.useRef(true);
  const reloadTimers = React.useRef<ReturnType<typeof setTimeout>[]>([]);
  const [previewFile, setPreviewFile] = React.useState<PreviewFile | undefined>(undefined);
  const [downloadingPath, setDownloadingPath] = React.useState<string | undefined>(undefined);
  /** Linked files whose blob no longer exists (preview / download answered 404) */
  const [unavailablePaths, setUnavailablePaths] = React.useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [unlinkRow, setUnlinkRow] = React.useState<DrawerStorageFile | undefined>(undefined);
  /** Pending rows whose register call is running ("Registrar" / "Registrar todos los pendientes") */
  const [registeringPaths, setRegisteringPaths] = React.useState<string[]>([]);
  /** Register calls that failed (shown in a banner until the next try) */
  const [registerFailures, setRegisterFailures] = React.useState<ComponentFunctions.RowRegisterResult[]>([]);

  const { open, target, fileListLoader, allowedFileTypes, initialFiles, GetRegisteredAzureFiles, GetTargetStorageFiles } =
    props;

  const clearReloadTimers = () => {
    reloadTimers.current.forEach((timer) => clearTimeout(timer));
    reloadTimers.current = [];
  };

  // Storage folders of the target, exactly as Contracts builds them (contract §2).
  const storageFolders = React.useMemo(
    () => getTargetStorageFolders(target, allowedFileTypes.map((type) => type.fileType)),
    [target.entityType, target.entityId, target.entityKey, target.owner?.ownerType, target.owner?.ownerId, target.owner?.protocolId, allowedFileTypes] // eslint-disable-line react-hooks/exhaustive-deps
  );

  /** Files from the storage (source of truth) + registered files (registration status). */
  const loadRegisteredFiles = React.useCallback(() => {
    GetRegisteredAzureFiles(fileListLoader, target);
    if (storageFolders.length > 0) GetTargetStorageFiles?.(target, storageFolders);
  }, [GetRegisteredAzureFiles, GetTargetStorageFiles, fileListLoader, storageFolders, target.entityType, target.entityId, target.entityKey]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearReloadTimers();
    };
  }, []);

  // Every time the drawer opens: start with the dropped files (if any) and refresh the registered list.
  React.useEffect(() => {
    if (!open) {
      clearReloadTimers();
      setItems([]);
      setShowRegisteringNotice(false);
      setPreviewFile(undefined);
      setPickerOpen(false);
      setUnlinkRow(undefined);
      setUnavailablePaths([]);
      setRegisteringPaths([]);
      setRegisterFailures([]);
      return;
    }
    props.ResolveEntityNames?.(ComponentFunctions.getTargetEntityRefs(target));
    setItems(
      ComponentFunctions.mergeUploadItems(
        [],
        ComponentFunctions.createUploadItems(initialFiles ?? [], allowedFileTypes),
        allowMultiple
      )
    );
    loadRegisteredFiles();
  }, [open, initialFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const incoming = ComponentFunctions.createUploadItems(files, allowedFileTypes);
      setItems((current) => ComponentFunctions.mergeUploadItems(current, incoming, allowMultiple));
    },
    [allowedFileTypes, allowMultiple]
  );

  const { isDraggingFiles, dropTargetProps } = useFileDropTarget({
    enabled: open && !isUploading,
    onFilesDropped: addFiles,
  });

  const updateItem = (id: string, changes: Partial<ComponentFunctions.UploadItem>) => {
    if (!mountedRef.current) return;
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  };

  const removeItem = (id: string) => setItems((current) => current.filter((item) => item.id !== id));

  const openPicker = () => {
    if (!isUploading) inputRef.current?.click();
  };

  const onDropAreaKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));
    // allow choosing the same file again
    event.target.value = '';
  };

  const isDone = (item: ComponentFunctions.UploadItem) => item.status === 'uploaded' || item.status === 'unregistered';
  const uploadable = ComponentFunctions.getUploadableItems(items, allowedFileTypes);
  const hasInvalidItems = items.some(
    (item) => !isDone(item) && !!ComponentFunctions.getItemValidation(item, allowedFileTypes)
  );

  // The owner service registers the file from the storage event: re-check after ~3s and ~10s.
  const scheduleRegisteredReload = () => {
    clearReloadTimers();
    reloadTimers.current = AZURE_UPLOAD_RECHECK_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        if (mountedRef.current) loadRegisteredFiles();
      }, delay)
    );
  };

  const uploadItems = async (toUpload: ComponentFunctions.UploadItem[]) => {
    if (isUploading || toUpload.length === 0) return;
    setIsUploading(true);
    let uploadedCount = 0;

    // Sequential: keeps progress readable and avoids saturating the connection.
    for (const item of toUpload) {
      updateItem(item.id, { status: 'uploading', progress: 0, uploadError: undefined });
      try {
        const result = await props.UploadAzureFile(
          { file: item.file, fileType: item.fileType as AzureFileTypeEnum, target },
          (percent: number) => updateItem(item.id, { progress: percent })
        );
        uploadedCount++;
        // Uploaded but not registered (contract §9.5): the blob stays; the user can retry only the register.
        if (result?.registered === false) {
          updateItem(item.id, {
            status: 'unregistered',
            progress: 100,
            relativePath: result.relativePath,
            registerError: ComponentFunctions.getUploadErrorMessage(result.registerError),
          });
        } else {
          updateItem(item.id, { status: 'uploaded', progress: 100, relativePath: result?.relativePath });
        }
      } catch (error: unknown) {
        const message = ComponentFunctions.getUploadErrorMessage(error);
        updateItem(item.id, { status: 'error', uploadError: message });
      }
    }

    if (!mountedRef.current) return;
    setIsUploading(false);
    if (uploadedCount > 0) {
      setShowRegisteringNotice(true);
      dispatchToast(
        <Toast>
          <ToastTitle>{t('azureFileUpload.messages.uploadedToast', { count: uploadedCount })}</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
      loadRegisteredFiles();
      scheduleRegisteredReload();
    }
  };

  /** "Reintentar registro": calls only the owner register endpoint for the uploaded blob. */
  const retryRegister = async (item: ComponentFunctions.UploadItem) => {
    if (!item.relativePath || item.fileType === undefined || item.registering) return;
    updateItem(item.id, { registering: true });
    try {
      await props.RegisterAzureFile(getRegisterKind(item.fileType), target, item.relativePath);
      updateItem(item.id, { status: 'uploaded', registering: false, registerError: undefined });
      loadRegisteredFiles();
    } catch (error: unknown) {
      updateItem(item.id, { registering: false, registerError: ComponentFunctions.getUploadErrorMessage(error) });
    }
  };

  const typeLabel = (fileType: AzureFileTypeEnum | undefined): string => {
    const allowed = allowedFileTypes.find((type) => type.fileType === fileType);
    return allowed ? String(t(allowed.labelKey)) : '';
  };

  const renderStatus = (status: ComponentFunctions.UploadItemStatus) => (
    <StatusPill
      color={statusBadge[status].color}
      icon={statusBadge[status].icon}
      data-testid={`azure-upload-status-${status}`}
    >
      {t(`azureFileUpload.status.${status}`)}
    </StatusPill>
  );

  const renderItem = (item: ComponentFunctions.UploadItem) => {
    const validation = !isDone(item) ? ComponentFunctions.getItemValidation(item, allowedFileTypes) : undefined;
    const locked = item.status === 'uploading' || isDone(item);

    return (
      <li key={item.id} className={styles.item} data-testid="azure-upload-item">
        <div className={styles.itemHeader}>
          <span className={styles.itemName} title={item.file.name}>
            {item.file.name}
          </span>
          <span className={styles.itemSize}>{formatFileSize(item.file.size)}</span>
          {renderStatus(item.status)}
          {!locked && (
            <Button
              appearance="subtle"
              size="small"
              icon={<Delete20Regular />}
              aria-label={t('azureFileUpload.buttons.remove')}
              title={t('azureFileUpload.buttons.remove')}
              onClick={() => removeItem(item.id)}
              disabled={isUploading}
            />
          )}
        </div>
        {allowedFileTypes.length > 1 ? (
          <Field
            label={t('azureFileUpload.fields.fileType')}
            required
            validationState={validation ? 'error' : 'none'}
            validationMessage={validation ? t(validation) : undefined}
          >
            <Dropdown
              className={styles.typeDropdown}
              aria-label={t('azureFileUpload.fields.fileType')}
              placeholder={t('azureFileUpload.fields.selectType')}
              value={typeLabel(item.fileType)}
              selectedOptions={item.fileType !== undefined ? [String(item.fileType)] : []}
              disabled={locked || isUploading}
              onOptionSelect={(_, data) =>
                updateItem(item.id, {
                  fileType: data.optionValue ? (Number(data.optionValue) as AzureFileTypeEnum) : undefined,
                })
              }
            >
              {allowedFileTypes.map((type) => {
                const label = String(t(type.labelKey));
                return (
                  <Option key={type.fileType} value={String(type.fileType)} text={label}>
                    {label}
                  </Option>
                );
              })}
            </Dropdown>
          </Field>
        ) : (
          validation && (
            <span className={styles.validation} role="alert">
              <ErrorCircle16Regular />
              {t(validation)}
            </span>
          )
        )}
        {(item.status === 'uploading' || item.status === 'uploaded') && (
          <ProgressBar
            value={item.progress / 100}
            thickness="large"
            color={item.status === 'uploaded' ? 'success' : 'brand'}
            aria-label={t(`azureFileUpload.status.${item.status}`)}
          />
        )}
        {item.status === 'unregistered' && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="azure-upload-unregistered">
            <MessageBarBody>
              <MessageBarTitle>{t('azureFileUpload.messages.registerErrorTitle')}</MessageBarTitle>
              {t('azureFileUpload.messages.registerError', { message: item.registerError ?? '' })}
            </MessageBarBody>
            <MessageBarActions>
              <Button
                appearance="transparent"
                size="small"
                onClick={() => retryRegister(item)}
                disabled={!!item.registering || !item.relativePath}
                icon={item.registering ? <Spinner size="extra-tiny" /> : undefined}
                data-testid="azure-upload-retry-register"
              >
                {t('azureFileUpload.buttons.retryRegister')}
              </Button>
            </MessageBarActions>
          </GuideMessageBar>
        )}
        {item.status === 'error' && (
          <GuideMessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>{t('azureFileUpload.messages.uploadErrorTitle')}</MessageBarTitle>
              {item.uploadError || t('azureFileUpload.messages.uploadError', { message: '' })}
            </MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" size="small" onClick={() => uploadItems([item])} disabled={isUploading}>
                {t('azureFileUpload.buttons.retry')}
              </Button>
            </MessageBarActions>
          </GuideMessageBar>
        )}
      </li>
    );
  };

  const translate = t as unknown as Translate;
  const targetLabel = ComponentFunctions.buildTargetLabel(target, translate, props.ownerNames ?? {});

  // Registration status only when the registered files could be read.
  const registeredForStatus =
    props.registeredFilesError || !props.registeredFilesLoaded ? undefined : props.registeredFiles;
  // Storage files of the target + files linked from other folders (contract §9.3).
  const storageRows = mergeDrawerRows(props.storageFiles ?? [], registeredForStatus, allowedFileTypes);
  const linkKind = canLinkFiles(fileListLoader) ? fileListLoader : undefined;
  const markUnavailable = (path: string, error: ErrorType | undefined) => {
    if (error?.ErrorCode !== 404) return;
    setUnavailablePaths((current) => (current.includes(path) ? current : [...current, path]));
  };
  const isUnavailable = (row: DrawerStorageFile) => row.status === 'linked' && unavailablePaths.includes(row.path);

  const linkFile = (path: string) => (linkKind ? props.LinkAzureFile(linkKind, target, path) : Promise.resolve());
  const onLinked = (count: number) => {
    loadRegisteredFiles();
    dispatchToast(
      <Toast>
        <ToastTitle>{t('azureFileUpload.links.linkedToast', { count })}</ToastTitle>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
  };

  const confirmUnlink = async () => {
    if (!unlinkRow?.registeredId || !linkKind) return;
    await props.UnlinkAzureFile(linkKind, target, unlinkRow.registeredId);
    dispatchToast(
      <Toast>
        <ToastTitle>{t('azureFileUpload.links.unlinkedToast', { name: getFriendlyFileName(unlinkRow.name) })}</ToastTitle>
      </Toast>,
      { intent: 'success', timeout: 5000 }
    );
    setUnlinkRow(undefined);
    loadRegisteredFiles();
  };
  const isLoadingList = !!props.isLoadingStorageFiles || props.isLoadingRegisteredFiles;

  const previewRow = (row: DrawerStorageFile) =>
    setPreviewFile({
      path: row.path,
      name: row.name,
      extension: row.extension,
      size: row.size,
      lastModified: row.lastModified,
    });

  const downloadRow = async (row: DrawerStorageFile) => {
    if (downloadingPath) return;
    setDownloadingPath(row.path);
    try {
      const sas = await props.RequestPreviewSas(row.path, true);
      triggerDownload(sas.sasUrl, row.name);
    } catch (error: unknown) {
      markUnavailable(row.path, error as ErrorType);
      const detail = ComponentFunctions.getUploadErrorMessage(error as ErrorType);
      dispatchToast(
        <Toast>
          <ToastTitle>{t('azureFileUpload.files.downloadError', { message: detail })}</ToastTitle>
        </Toast>,
        { intent: 'error', timeout: 5000 }
      );
    } finally {
      if (mountedRef.current) setDownloadingPath(undefined);
    }
  };

  // ---- "Registrar" on pending rows (spec 005, US5): the storage event may never arrive ----
  const pendingRows = ComponentFunctions.getPendingRows(storageRows);
  const isRegisteringRows = registeringPaths.length > 0;

  const registerPendingRows = async (rows: DrawerStorageFile[]) => {
    const toRegister = rows.filter((row) => !registeringPaths.includes(row.path));
    if (toRegister.length === 0) return;
    const paths = toRegister.map((row) => row.path);
    setRegisteringPaths((current) => [...current, ...paths]);
    setRegisterFailures((current) => current.filter((failure) => !paths.includes(failure.path)));
    const results = await ComponentFunctions.registerRowsSequentially(
      toRegister,
      (row) => props.RegisterAzureFile(getRegisterKind(row.fileType), target, row.path),
      (result) => {
        if (!mountedRef.current) return;
        setRegisteringPaths((current) => current.filter((path) => path !== result.path));
        if (result.status === 'error') setRegisterFailures((current) => [...current, result]);
      }
    );
    if (!mountedRef.current) return;
    const done = results.filter((result) => result.status !== 'error').length;
    const failed = results.length - done;
    if (done > 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>
            {results.length === 1
              ? t('azureFileUpload.register.registeredToast', { name: getFriendlyFileName(results[0].name) })
              : t('azureFileUpload.register.registeredManyToast', { count: done })}
          </ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 5000 }
      );
    }
    if (failed > 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>{t('azureFileUpload.register.failedToast', { count: failed })}</ToastTitle>
        </Toast>,
        { intent: 'error', timeout: 5000 }
      );
    }
    if (done > 0) loadRegisteredFiles();
  };

  const renderRegisterFailures = () =>
    registerFailures.length > 0 && (
      <GuideMessageBar intent="error" layout="multiline" data-testid="azure-register-failures">
        <MessageBarBody>
          <MessageBarTitle>{t('azureFileUpload.register.failedTitle', { count: registerFailures.length })}</MessageBarTitle>
          <ul className={styles.failureList}>
            {registerFailures.map((failure) => (
              <li key={failure.path}>
                {t('azureFileUpload.register.failedItem', {
                  name: getFriendlyFileName(failure.name),
                  message: failure.message || t('azureFileUpload.register.unknownError'),
                })}
              </li>
            ))}
          </ul>
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="transparent" size="small" onClick={() => setRegisterFailures([])}>
            {t('azureFileUpload.register.dismiss')}
          </Button>
        </MessageBarActions>
      </GuideMessageBar>
    );

  const renderRegistrationStatus = (row: DrawerStorageFile) => {
    if (row.status === 'linked') {
      const location = buildFriendlyLocation(row.path, translate, props.ownerNames ?? {}) || t('filesExplorer.root');
      return (
        <span className={styles.statusGroup}>
          <Tooltip
            content={t('azureFileUpload.links.locationTooltip', { location, path: row.location || '/' })}
            relationship="description"
            withArrow
          >
            <span className={styles.statusTrigger} tabIndex={0}>
              <StatusPill
                color="brand"
                icon={<Link16Regular />}
                data-testid="azure-file-status-linked"
              >
                {t('azureFileUpload.links.linked')}
              </StatusPill>
            </span>
          </Tooltip>
          {isUnavailable(row) && (
            <StatusPill
              color="danger"
              icon={<DocumentDismiss16Regular />}
              data-testid="azure-file-status-unavailable"
            >
              {t('azureFileUpload.links.unavailable')}
            </StatusPill>
          )}
        </span>
      );
    }
    if (row.status === 'registered') {
      return (
        <StatusPill
          color="success"
          icon={<CheckmarkCircle16Regular />}
          data-testid="azure-file-status-registered"
        >
          {t('azureFileUpload.files.status.registered')}
        </StatusPill>
      );
    }
    if (row.status === 'pending') {
      return (
        <Tooltip content={t('azureFileUpload.files.status.pendingHelp')} relationship="description" withArrow>
          <span className={styles.statusTrigger} tabIndex={0}>
            <StatusPill
              color="warning"
              icon={<Clock16Regular />}
              data-testid="azure-file-status-pending"
            >
              {t('azureFileUpload.files.status.pending')}
            </StatusPill>
          </span>
        </Tooltip>
      );
    }
    return <span className={styles.secondaryText}>—</span>;
  };

  const renderRegisteredFiles = () => {
    if (props.storageFilesError && storageRows.length === 0) {
      return (
        <GuideMessageBar intent="error" layout="multiline" data-testid="azure-storage-error">
          <MessageBarBody>
            <MessageBarTitle>{t('azureFileUpload.files.loadErrorTitle')}</MessageBarTitle>
            {ComponentFunctions.getUploadErrorMessage(props.storageFilesError) || t('azureFileUpload.files.loadError')}
          </MessageBarBody>
          <MessageBarActions>
            <Button appearance="transparent" size="small" onClick={loadRegisteredFiles}>
              {t('azureFileUpload.buttons.retry')}
            </Button>
          </MessageBarActions>
        </GuideMessageBar>
      );
    }
    if (storageRows.length === 0 && storageFolders.length > 0 && (isLoadingList || !props.storageFilesLoaded)) {
      return (
        <Skeleton
          aria-label={t('azureFileUpload.registered.loading')}
          className={styles.skeleton}
          data-testid="azure-registered-skeleton"
        >
          <SkeletonItem size={16} />
          <SkeletonItem size={16} />
          <SkeletonItem size={16} />
        </Skeleton>
      );
    }
    if (storageRows.length === 0) {
      return (
        <div className={styles.emptyState} data-testid="azure-registered-empty">
          <DocumentBulletList24Regular className={styles.emptyIcon} />
          <span className={styles.emptyTitle}>{t('azureFileUpload.registered.emptyTitle')}</span>
          <span className={styles.emptyText}>{t('azureFileUpload.files.emptyHelp')}</span>
          <Button appearance="primary" icon={<ArrowUpload24Regular />} onClick={openPicker} disabled={isUploading}>
            {t('azureFileUpload.buttons.selectFiles')}
          </Button>
        </div>
      );
    }
    return (
      <>
        {renderRegisterFailures()}
        {props.registeredFilesError && (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="azure-registered-warning">
            <MessageBarBody>{t('azureFileUpload.files.statusUnavailable')}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" size="small" onClick={loadRegisteredFiles}>
                {t('azureFileUpload.buttons.retry')}
              </Button>
            </MessageBarActions>
          </GuideMessageBar>
        )}
        {/* Grilla de la guía con tres columnas: en un drawer angosto las seis columnas anteriores se
            pisaban. Tipo, fecha y tamaño van como línea secundaria debajo del nombre. */}
        <GuideTable ariaLabel={t('azureFileUpload.files.title')} data-testid="azure-storage-files">
          <thead>
            <tr>
              <GuideTh>{t('azureFileUpload.registered.columns.name')}</GuideTh>
              <GuideTh>{t('azureFileUpload.files.columns.status')}</GuideTh>
              <GuideTh className={cell('actions')} align="end">{t('azureFileUpload.files.columns.actions')}</GuideTh>
            </tr>
          </thead>
          <tbody>
            {storageRows.map((row) => {
              const friendly = getFriendlyFileName(row.name);
              const meta = [
                t(row.typeLabelKey),
                row.lastModified ? moment(row.lastModified).format('DD/MM/YYYY HH:mm') : undefined,
                formatFileSize(row.size),
              ].filter(Boolean).join(' · ');
              return (
                <tr key={row.key} className={guideRowClassName({ pending: row.status === 'pending' })} data-testid="azure-storage-file-row">
                  <td>
                    <div className={styles.fileCell}>
                      <Tooltip content={row.name} relationship="description" withArrow>
                        <span className={styles.fileName} tabIndex={0}>
                          {friendly}
                        </span>
                      </Tooltip>
                      <span className={styles.fileMeta}>{meta}</span>
                    </div>
                  </td>
                  <td>{renderRegistrationStatus(row)}</td>
                  <td className={cell('actions')}>
                    <div className={styles.rowActions}>
                      {row.status === 'pending' && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={registeringPaths.includes(row.path) ? <Spinner size="extra-tiny" /> : <DocumentCheckmark20Regular />}
                          onClick={() => registerPendingRows([row])}
                          disabled={registeringPaths.includes(row.path) || isUploading}
                          aria-label={t('azureFileUpload.register.registerNamed', { name: friendly })}
                          title={t('azureFileUpload.register.registerHelp')}
                          data-testid="azure-file-register"
                        >
                          {t('azureFileUpload.register.register')}
                        </Button>
                      )}
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<Eye20Regular />}
                        onClick={() => previewRow(row)}
                        aria-label={t('azureFileUpload.files.previewNamed', { name: friendly })}
                        title={t('azureFileUpload.files.preview')}
                        data-testid="azure-file-preview"
                      />
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={downloadingPath === row.path ? <Spinner size="extra-tiny" /> : <ArrowDownload20Regular />}
                        onClick={() => downloadRow(row)}
                        disabled={!!downloadingPath}
                        aria-label={t('azureFileUpload.files.downloadNamed', { name: friendly })}
                        title={t('azureFileUpload.files.download')}
                        data-testid="azure-file-download"
                      />
                      {row.status === 'linked' && linkKind && (
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<LinkDismiss20Regular />}
                          onClick={() => setUnlinkRow(row)}
                          aria-label={t('azureFileUpload.links.unlinkNamed', { name: friendly })}
                          title={t('azureFileUpload.links.unlink')}
                          data-testid="azure-file-unlink"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </GuideTable>
      </>
    );
  };

  return (
    <>
    <GuideDrawer
      open={props.open}
      size="large"
      onDismiss={props.onDismiss}
      preventDismiss={isUploading}
      closeLabel={t('azureFileUpload.buttons.close')}
      eyebrow={
        targetLabel ? (
          <span
            className={styles.targetLabel}
            title={getOwnerFolderPath(target.owner)}
            data-testid="azure-upload-target-label"
          >
            {targetLabel}
          </span>
        ) : undefined
      }
      title={props.title}
      secondaryAction={{ label: t('azureFileUpload.buttons.close') }}
      extraActions={
        linkKind
          ? [
              {
                label: t('azureFileUpload.links.linkExisting'),
                icon: <Link20Regular />,
                onClick: () => setPickerOpen(true),
                disabled: isUploading,
                testId: 'azure-link-existing',
              },
            ]
          : undefined
      }
      primaryAction={{
        label: t('azureFileUpload.buttons.uploadAll'),
        icon: <ArrowUpload24Regular />,
        onClick: () => uploadItems(uploadable),
        loading: isUploading,
        disabled: uploadable.length === 0,
      }}
    >
      <Toaster toasterId={toasterId} position="bottom-start" />
      <div
        className={mergeClasses(
          styles.dropArea,
          isDraggingFiles && styles.dropAreaActive,
          isUploading && styles.dropAreaDisabled
        )}
        role="button"
        tabIndex={isUploading ? -1 : 0}
        aria-disabled={isUploading}
        aria-label={t('azureFileUpload.dropZone.ariaLabel')}
        data-testid="azure-upload-drop-area"
        onClick={openPicker}
        onKeyDown={onDropAreaKeyDown}
        {...dropTargetProps}
      >
        <ArrowUpload24Regular className={styles.dropIcon} />
        <span className={styles.dropTitle}>{t('azureFileUpload.dropZone.hint')}</span>
        <Text className={styles.hint}>{t('azureFileUpload.dropZone.orClick')}</Text>
        <span className={styles.hint}>
          {t('azureFileUpload.dropZone.allowedExtensions', {
            extensions: listAllowedExtensions(allowedFileTypes).join(', '),
          })}
        </span>
        <input
          ref={inputRef}
          type="file"
          data-testid="azure-upload-input"
          className={styles.hiddenInput}
          accept={buildAcceptAttribute(allowedFileTypes)}
          multiple={allowMultiple}
          onChange={onInputChange}
          onClick={(event) => event.stopPropagation()}
          tabIndex={-1}
        />
      </div>

      {showRegisteringNotice && (
        <GuideMessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>{t('azureFileUpload.messages.uploadedTitle')}</MessageBarTitle>
            {t('azureFileUpload.messages.uploadedRegistering')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {items.length > 0 && <ul className={styles.itemList}>{items.map(renderItem)}</ul>}

      {hasInvalidItems && (
        <GuideMessageBar intent="warning">
          <MessageBarBody>{t('azureFileUpload.messages.someInvalid')}</MessageBarBody>
        </GuideMessageBar>
      )}

      <GuideDrawerSection
        title={t('azureFileUpload.files.title')}
        actions={
          <>
            {!isUploading && <ViewInFilesLink owner={target.owner} onNavigate={props.onDismiss} />}
            {pendingRows.length > 1 && (
              <Button
                appearance="subtle"
                icon={isRegisteringRows ? <Spinner size="extra-tiny" /> : <DocumentCheckmark20Regular />}
                onClick={() => registerPendingRows(pendingRows)}
                disabled={isRegisteringRows || isUploading}
                data-testid="azure-register-all"
              >
                {t('azureFileUpload.register.registerAll', { count: pendingRows.length })}
              </Button>
            )}
            <Button
              appearance="subtle"
              icon={isLoadingList ? <Spinner size="extra-tiny" /> : <ArrowClockwise20Regular />}
              onClick={loadRegisteredFiles}
              disabled={isLoadingList}
            >
              {t('azureFileUpload.buttons.refresh')}
            </Button>
          </>
        }
      >
        {renderRegisteredFiles()}
      </GuideDrawerSection>
    </GuideDrawer>
    {/* Sibling (not child) of the upload drawer: it stacks over it and Esc only closes the preview. */}
    <FilePreview
      open={!!previewFile}
      file={previewFile}
      onDismiss={() => setPreviewFile(undefined)}
      onFileError={markUnavailable}
    />
    {linkKind && (
      <FilePickerDialog
        open={pickerOpen}
        onDismiss={() => setPickerOpen(false)}
        title={t('azureFileUpload.links.pickerTitle', { target: targetLabel })}
        initialPath={getOwnerFolderPath(target.owner) ?? ''}
        linkFile={linkFile}
        onLinked={onLinked}
      />
    )}
    <ConfirmActionDialog
      open={!!unlinkRow}
      testId="azure-unlink-dialog"
      title={t('azureFileUpload.links.unlinkTitle')}
      message={t('azureFileUpload.links.unlinkMessage', {
        name: unlinkRow ? getFriendlyFileName(unlinkRow.name) : '',
        target: targetLabel,
      })}
      warning={t('azureFileUpload.links.unlinkWarning')}
      confirmLabel={t('azureFileUpload.links.unlink')}
      errorTitle={t('azureFileUpload.links.unlinkErrorTitle')}
      onConfirm={confirmUnlink}
      onDismiss={() => setUnlinkRow(undefined)}
      describeError={(error) =>
        describeStorageError(
          error,
          {},
          // Provider files (spec 005): 409 = consumptions were imported from the file. The API message is shown;
          // without one, the explanation of why the link must stay.
          linkKind === 'provider' && error?.ErrorCode === 409
            ? t('azureFileUpload.links.unlinkProviderInUse')
            : t('filesExplorer.messages.operationError')
        )
      }
    />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(AzureFileUploadDrawerComponent as React.ComponentType<ComponentFunctions.Props>);
